import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Ear, Pause, Play, RotateCcw, SkipForward, Star } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { BeatRow } from '@/components/BeatRow';
import { ChordDiagram } from '@/components/ChordDiagram';
import { LevelWave } from '@/components/LevelWave';
import { Button, Card } from '@/components/ui';
import { getChord } from '@/data/chords';
import { t } from '@/i18n';
import { audioEngine } from '@/audio/engine/AudioEngine';
import {
  useAudioLifecycle,
  useAudioState,
  useEngineStore,
  useMicrophoneLease,
} from '@/hooks/useAudio';
import { useFinishSession } from '@/hooks/useFinishSession';
import { ChordChangeEngine, type ChordChangeSnapshot } from '@/training/chordChange/ChordChangeEngine';
import type { ChordChangeSettings } from '@/types/training';
import './ActivePage.css';

/**
 * The screen the player actually looks at while holding a guitar.
 *
 * Reading order is fixed by the physical situation: chord name (readable across
 * a room), diagram, what is coming next, the beat, then feedback. Nothing on
 * this screen is a chart or a number that needs interpreting mid-bar.
 */
export function ChordChangeActivePage() {
  const location = useLocation();
  const settings = (location.state as { settings?: ChordChangeSettings } | null)?.settings;

  const audio = useAudioState();
  useMicrophoneLease();
  const engineRef = useRef<ChordChangeEngine | null>(null);
  const [engine, setEngine] = useState<ChordChangeEngine | null>(null);

  const handleFinished = useFinishSession();

  useEffect(() => {
    if (!settings) return;
    if (audio.status !== 'ready') return;
    if (engineRef.current) return;

    const created = new ChordChangeEngine(audioEngine, settings, handleFinished);
    engineRef.current = created;
    setEngine(created);
    created.start();

    return () => {
      created.dispose();
      engineRef.current = null;
    };
  }, [settings, audio.status, handleFinished]);

  // Backgrounding the tab suspends the AudioContext; carrying on would record
  // silence and call it a failed chord.
  useAudioLifecycle(
    useCallback(() => {
      engineRef.current?.pause();
    }, []),
  );

  if (!settings) {
    return (
      <AppShell title={t('modes.chordChange')} showBack>
        <Card>
          <p className="muted">{t('setup.needTwoChords')}</p>
        </Card>
      </AppShell>
    );
  }

  if (audio.status !== 'ready' || !engine) {
    return (
      <AppShell title={t('modes.chordChange')} showBack>
        <Card>
          <p className="muted">{t('mic.checking')}</p>
        </Card>
      </AppShell>
    );
  }

  return <ActiveSession engine={engine} settings={settings} />;
}

interface ActiveSessionProps {
  engine: ChordChangeEngine;
  settings: ChordChangeSettings;
}

function ActiveSession({ engine, settings }: ActiveSessionProps) {
  const snapshot = useEngineStore(engine.store);
  const chord = useMemo(() => getChord(snapshot.chordId), [snapshot.chordId]);

  const totalMin = settings.durationMin;
  const doneMin = Math.min(totalMin, Math.floor(snapshot.elapsedSec / 60));
  const paused = snapshot.phase === 'paused';
  const counting = snapshot.phase === 'countIn';

  return (
    <AppShell
      title={t('modes.chordChange')}
      showBack
      // Leaving an exercise finishes it: everything practised so far is real
      // and must be saved. Navigating away silently would discard the session.
      onBack={() => engine.finish()}
      headerRight={
        <div className="active__header-right">
          <span className="active__timer tnum">
            {t('active.timeShort', { done: doneMin, total: totalMin })}
          </span>
          {/* While the pause overlay is up it owns the resume action. Leaving
              this button mounted would put a focusable control behind a
              covering layer, which keyboard users would tab straight into. */}
          {!paused && (
            <button
              type="button"
              className="btn btn--icon"
              onClick={() => engine.pause()}
              aria-label={t('active.pause')}
            >
              <Pause size={20} aria-hidden="true" />
            </button>
          )}
        </div>
      }
    >
      <Card section className="active__stage">
        {snapshot.phase === 'countIn' ? (
          <div className="active__countin" aria-live="polite">
            <span className="active__countin-number tnum">{snapshot.countInBeat}</span>
            <span className="active__countin-label">{t('active.countIn')}</span>
          </div>
        ) : (
          <>
            <h2 className="active__chord">{chord?.name ?? snapshot.chordId}</h2>
            <p className="active__prompt">{t('active.playNow')}</p>
            {chord && (
              <div className="active__diagram">
                <ChordDiagram chord={chord} size={170} className="chord-diagram--accent" />
              </div>
            )}
            <p className="active__next">
              {t('active.next', { chord: snapshot.nextChordId })
                .split(':')
                .map((part, index) =>
                  index === 0 ? (
                    <span key="label">{part}: </span>
                  ) : (
                    <strong key="value">{part.trim()}</strong>
                  ),
                )}
            </p>
            <BeatRow
              count={settings.beatsPerChord}
              active={snapshot.beatInSlot}
              bpm={snapshot.bpm}
            />
            <p className="active__bpm tnum">
              {t('setup.bpm', { value: snapshot.bpm })}
              {snapshot.tempoHint && (
                <span className="active__tempo-hint">
                  {snapshot.tempoHint === 'up' ? ' ↑' : ' ↓'}
                </span>
              )}
            </p>
          </>
        )}
      </Card>

      {/* During the count-in there is no slot yet: "repeat" and "next" would
          act on nothing, and the listening strip would claim to be judging
          silence. The screen shows the count and nothing else. */}
      {!counting && (
        <>
          <div className={`listening${snapshot.listening ? ' listening--on' : ''}`}>
            <span className="listening__badge">
              <Ear size={20} aria-hidden="true" />
            </span>
            <span className="listening__label">{t('active.listening')}</span>
            <LevelWave bars={30} height={26} paused={paused} />
          </div>

          <div aria-live="polite" className="active__feedback-slot">
            {snapshot.verdict && <VerdictCard snapshot={snapshot} />}
            {/* Two slots with no guitar heard: say so calmly. Staying silent
                while the player strums is the most confusing thing the app can
                do, and it is not the player's mistake. */}
            {!snapshot.verdict && snapshot.hearingNothing && (
              <p className="active__hint">{t('gate.hearingNothing')}</p>
            )}
          </div>

          <div className="active__actions">
            <Button variant="secondary" onClick={() => engine.repeat()}>
              <RotateCcw size={18} aria-hidden="true" />
              {t('active.repeat')}
            </Button>
            <Button onClick={() => engine.skip()}>
              <SkipForward size={18} aria-hidden="true" />
              {t('active.skip')}
            </Button>
          </div>
        </>
      )}

      {paused && (
        <div className="active__paused" role="status">
          <p className="active__paused-title">{t('active.paused')}</p>
          <Button onClick={() => engine.resume()}>
            <Play size={18} fill="currentColor" aria-hidden="true" />
            {t('active.resume')}
          </Button>
          {/* Leaving from the pause overlay saves too — the same rule as the
              back arrow. Nothing the player actually did gets thrown away. */}
          <button type="button" className="active__exit" onClick={() => engine.finish()}>
            {t('active.exit')}
          </button>
        </div>
      )}
    </AppShell>
  );
}

function VerdictCard({ snapshot }: { snapshot: ChordChangeSnapshot }) {
  const verdict = snapshot.verdict;
  if (!verdict) return null;

  const tone =
    verdict === 'correct'
      ? 'good'
      : verdict === 'almost'
        ? 'warn'
        : verdict === 'uncertain'
          ? 'neutral'
          : 'bad';

  return (
    <div className={`verdict verdict--${tone}`}>
      <span className="verdict__icon">
        <Star size={20} fill="currentColor" aria-hidden="true" />
      </span>
      <div className="verdict__body">
        <p className="verdict__title">{t(`verdict.${verdict}`)}</p>
        {snapshot.feedback.length > 0 && (
          <ul className="verdict__list">
            {snapshot.feedback.map((item, index) => (
              <li key={`${item.key}-${index}`}>{t(item.key, item.params)}</li>
            ))}
          </ul>
        )}
        {snapshot.lastTransitionSec !== null && verdict === 'correct' && (
          <p className="verdict__time tnum">
            {snapshot.lastTransitionSec.toFixed(2)} {t('common.seconds')}
          </p>
        )}
      </div>
    </div>
  );
}
